import { FC, useEffect, useState } from 'react';
import { Button, Alert } from '@/components/ui';
import { Calendar, Trash2 } from 'lucide-react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';

interface Step7CalendlyProps {
  initialData?: any;
  onNext: (data: any) => void;
  onBack?: () => void;
  isSubmitting?: boolean;
}

export const Step7Calendly: FC<Step7CalendlyProps> = ({
  onNext,
  onBack,
  isSubmitting = false,
}) => {
  const [calendarStatus, setCalendarStatus] = useState<{
    connected: boolean;
    provider?: 'google' | 'outlook';
    calendarId?: string;
    connectedAt?: string;
  } | null>(null);
  const [isCalendarLoading, setIsCalendarLoading] = useState(true);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const fetchCalendarStatus = async () => {
    try {
      setIsCalendarLoading(true);
      // Check Google
      try {
        const { data: googleData } = await api.get('/auth/google-calendar/status');
        if (googleData.connected) {
          setCalendarStatus({ ...googleData, provider: 'google' });
          return;
        }
      } catch (e) {
        // Ignore
      }
      setCalendarStatus({ connected: false });
    } catch (err: unknown) {
      console.error('Failed to fetch calendar status:', err);
      setCalendarError(getErrorMessage(err));
    } finally {
      setIsCalendarLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarStatus();

    const params = new URLSearchParams(window.location.search);
    if (params.get('google_calendar_success') === 'true') {
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchCalendarStatus();
    } else if (params.get('google_calendar_error')) {
      setCalendarError(`Failed to connect Google Calendar: ${params.get('google_calendar_error')}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleConnectGoogle = async () => {
    try {
      setIsConnecting(true);
      setCalendarError(null);
      const { data } = await api.post('/auth/google-calendar-connect', {
        redirectUrl: '/profile?tab=wizard&step=8'
      });
      if (data?.authUrl) window.location.href = data.authUrl;
      else throw new Error('No authorization URL returned.');
    } catch (err: unknown) {
      console.error('Failed to initiate Google connection:', err);
      setCalendarError(getErrorMessage(err));
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!calendarStatus?.provider || !window.confirm(`Are you sure you want to disconnect your ${calendarStatus.provider === 'google' ? 'Google' : 'Outlook'} Calendar?`)) return;

    try {
      setIsDisconnecting(true);
      await api.delete(calendarStatus.provider === 'google' ? '/auth/google-calendar/disconnect' : '/auth/outlook-calendar/disconnect');
      setCalendarStatus({ connected: false });
    } catch (err: unknown) {
      console.error('Failed to disconnect calendar:', err);
      setCalendarError(getErrorMessage(err));
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Google Calendar Integration</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Connect your calendar to synchronize availability and prevent booking conflicts.
          </p>
        </div>

        {calendarError && (
          <Alert variant="error" message={calendarError} onDismiss={() => setCalendarError(null)} />
        )}

        {isCalendarLoading ? (
          <div className="h-20 flex items-center justify-center bg-slate-50 border border-slate-200 rounded-lg">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-slate-600"></div>
          </div>
        ) : (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${calendarStatus?.connected ? 'bg-green-100 text-green-600' : 'bg-white text-slate-400 border border-slate-200'}`}>
                  <Calendar className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-slate-900">
                    {calendarStatus?.connected
                      ? `${calendarStatus.provider === 'google' ? 'Google' : 'Outlook'} Calendar`
                      : 'Not Connected'}
                  </h4>
                  {calendarStatus?.connected ? (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="flex h-2 w-2 rounded-full bg-green-600"></span>
                      <p className="text-xs text-green-700 font-medium">Synced and Active</p>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 mt-1">Connect your calendar to get started</p>
                  )}
                </div>
              </div>

              {calendarStatus?.connected ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleDisconnect}
                  isLoading={isDisconnecting}
                  disabled={isDisconnecting}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Disconnect
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleConnectGoogle}
                  isLoading={isConnecting}
                  disabled={isConnecting}
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" />
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Connect Google Calendar
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
        <div className="flex gap-4">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600">
            <Calendar className="w-5 h-5" />
          </div>
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Why connect your calendar?</p>
            <p className="leading-relaxed opacity-90">
              Connecting your primary calendar allows the system to check your availability in real-time. This prevents automated bookings from conflicting with your existing appointments or personal time.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-4">
        {onBack && (
          <Button type="button" variant="secondary" onClick={onBack} className="mr-3">
            Back
          </Button>
        )}
        <Button
          variant="primary"
          onClick={() => onNext({})}
          isLoading={isSubmitting}
          className="px-8"
        >
          {calendarStatus?.connected ? 'Finalize Setup' : 'Skip / Complete'}
        </Button>
      </div>
    </div>
  );
};
